<script setup lang="ts">
import type {
  NewProduct,
  NewProductModification,
} from '@/types/api/api-v4/product'
import type {
  ProductV5,
  ProductV5Modification,
} from '@/types/api/api-v5/product'

import ProductCardImageTags from '@/components/Product/ProductCard/ProductCardImageTags.vue'

type Props = {
  product: NewProduct | ProductV5
  currentModification: NewProductModification | ProductV5Modification
  productsQuantity: number
  isLoading?: boolean
}
const props = defineProps<Props>()

const emit = defineEmits(['on-open-dialog'])

const { productImage, productTitle, setError } = useProductImage(props)

const imageClasses = computed(() => {
  const classes = ['product-card-image']

  if (props.productsQuantity > 0) classes.push('product-card-image--overlay')

  if (props.isLoading) classes.push('product-card-image--loading')

  return classes
})

const productsQuantityAmount = computed(() => {
  return props.productsQuantity > 0 ? props.productsQuantity : ''
})

const onClick = () => emit('on-open-dialog')
</script>

<template>
  <a
    :href="`/product/${product.slug}/`"
    :class="imageClasses"
    :data-amount="productsQuantityAmount"
    @click.prevent="onClick"
  >
    <product-card-image-tags :product="product" />

    <nuxt-img
      :src="productImage"
      :title="productTitle"
      :alt="product.name"
      class="product-card-image__media"
      loading="lazy"
      format="webp"
      @error="setError(true)"
    />
  </a>
</template>

<style lang="scss" scoped>
.product-card-image {
  position: relative;
  height: 121px;
  overflow: hidden;
  border-radius: 16px;

  @include media('>=desktop-mini') {
    height: 185px;
  }

  &::after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
    display: flex;
    visibility: hidden;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: 800;
    line-height: 26px;
    color: var(--ui-white);
    pointer-events: none;
    content: attr(data-amount);
    background-color: rgba(44, 47, 57, 0.6);
    opacity: 0;
    transition:
      opacity 0.2s ease-in-out,
      visibility 0.2s ease-in-out;

    @include media('>=desktop-mini') {
      font-size: 28px;
      font-weight: 900;
      line-height: 36px;
    }
  }

  &--loading {
    opacity: 0;
  }

  &--overlay {
    &::after {
      visibility: visible;
      pointer-events: auto;
      opacity: 1;
    }
  }

  &__quantity {
    padding: 2px 4px;
    line-height: 10px;

    @include media('>=desktop-mini') {
      line-height: 16px;
    }
  }

  &__media {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
</style>
